import { useState, useEffect } from 'react';
import { Shield, AlertCircle } from 'lucide-react';
import { GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';

interface AdminPasswordGuardProps {
  children: React.ReactNode;
}

// Admin access is granted by Google Sign-In on this account only — never by a password in code.
const ADMIN_EMAIL = 'viitamint@gmail.com';
const SESSION_KEY = 'admin_authenticated';
const SESSION_DURATION = 3600000; // 1 hour in milliseconds

// Admin lives on a single canonical domain (coursedee.web.app). The same SPA is also served on
// saledi.web.app and host อื่น ๆ, so /admin would otherwise open there too — with a separate
// login per origin. Any non-coursedee host is redirected to coursedee.web.app so there is one place to log in.
const ADMIN_ALLOWED_HOSTS = [
  'coursedee.web.app',
  'xn--42c6a5b2an5cxj.com', 'www.xn--42c6a5b2an5cxj.com', // คอร์สดี.com
  'edumi-4dfbe.web.app', 'edumi-4dfbe.firebaseapp.com',
  'localhost', '127.0.0.1',
];
const isAllowedAdminHost = () =>
  typeof window === 'undefined' || ADMIN_ALLOWED_HOSTS.includes(window.location.hostname);

export default function AdminPasswordGuard({ children }: AdminPasswordGuardProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [redirecting, setRedirecting] = useState(!isAllowedAdminHost());

  useEffect(() => {
    if (!isAllowedAdminHost()) {
      setRedirecting(true);
      window.location.replace('https://coursedee.web.app' + window.location.pathname + window.location.search);
    }
  }, []);

  useEffect(() => {
    // Check if already authenticated in this session
    const authData = sessionStorage.getItem(SESSION_KEY);
    if (authData) {
      try {
        const { timestamp } = JSON.parse(authData);
        const now = Date.now();

        // Check if session is still valid (within 1 hour)
        if (now - timestamp < SESSION_DURATION) {
          setIsAuthenticated(true);
        } else {
          sessionStorage.removeItem(SESSION_KEY);
        }
      } catch (e) {
        sessionStorage.removeItem(SESSION_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  const handleGoogleLogin = async () => {
    setError('');
    setIsSigningIn(true);

    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(auth, provider);

      const email = (result.user.email || '').toLowerCase();
      if (email !== ADMIN_EMAIL) {
        // Wrong account — drop the session immediately so it can't reach admin data.
        await signOut(auth);
        setError('คุณไม่มีสิทธิ์ใช้งาน !');
        return;
      }

      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        timestamp: Date.now(),
        username: email,
      }));
      setIsAuthenticated(true);
    } catch (error: any) {
      console.error('❌ Google sign-in failed:', error);
      if (error?.code !== 'auth/popup-closed-by-user' && error?.code !== 'auth/cancelled-popup-request') {
        setError(`เข้าสู่ระบบล้มเหลว: ${error.message}`);
      }
    } finally {
      setIsSigningIn(false);
    }
  };

  if (redirecting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">กำลังพาไปที่ coursedee.web.app/admin...</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">กำลังตรวจสอบ...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
                Admin Access
              </h1>
              <p className="text-gray-500">เข้าสู่ระบบด้วยบัญชี Google ผู้ดูแลระบบ</p>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl mb-6">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={isSigningIn}
              className="w-full py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:shadow-lg hover:border-gray-300 transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-60"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              {isSigningIn ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบด้วย Google'}
            </button>

            {/* Footer */}
            <div className="mt-6 text-center">
              <p className="text-xs text-gray-400">
                🔒 ระบบป้องกันการเข้าถึงโดยไม่ได้รับอนุญาต
              </p>
              <p className="text-xs text-gray-400 mt-1">
                ผู้ดูแลระบบ: {ADMIN_EMAIL}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
